<nav class="nav" [class.nav--minimal]="minimal">
  <section class="nav-fixed">
    <div class="logo">
      <a
        routerLink="/organisations/{{
          currentOrganisation?.organisationId || '-'
        }}/registers"
      >
        <picture>
          <source
            srcset="/resources/images/fq-fixed-assets-logo-color.svg"
            media="(min-width: 900px)"
          />
          <img
            src="/resources/images/fq-fixed-assets-icon-color.png"
            height="26"
            alt="FinQuery Fixed Assets"
          />
        </picture>
      </a>
    </div>

    @if (appVersion) {
      <div class="nav-version text-end">
        <button
          type="button"
          class="btn-version badge border-0 rounded-pill text-white-50"
          (click)="openReleaseNotes()"
        >
          v{{ appVersion }}

          @if (newFeatureCount; as count) {
            <span class="badge rounded-pill bg-secondary float-end ms-1">
              @if (count < 10) {
                {{ count }}
              } @else {
                9+
              }
            </span>
          }
        </button>

        @if (isVersionPending) {
          <button
            type="button"
            class="badge bg-success border-0 ms-1"
            (click)="updateVersion()"
          >
            Update
          </button>
        }
      </div>
    }

    <div
      class="position-absolute d-flex gap-2"
      [hidden]="!isStagingUi && !isStagingApi"
    >
      <a
        id="staging-ui"
        [href]="prodUiLink"
        class="badge bg-warning text-decoration-none"
        title="Switch to live UI"
        [hidden]="!isStagingUi"
      >
        Staging UI
      </a>
      <a
        id="staging-api"
        href="?UseStagingApi=0"
        class="badge bg-warning text-decoration-none"
        title="Switch to live API"
        [hidden]="!isStagingApi"
      >
        Staging API
      </a>
    </div>
  </section>

  @if (!hasNoOrganisation) {
    <section class="nav-fixed pb-0 org-register">
      <app-edit-organisation-modal
        #editOrganisationModal
      ></app-edit-organisation-modal>

      @if (currentRegister) {
        <label>Entity & Register</label>
      }
      @if (currentOrganisation && !hasOnlyDemoRegister) {
        @if (currentRegister) {
          <div ngbDropdown>
            <button
              type="button"
              id="register-selection"
              class="btn btn-register"
              ngbDropdownToggle
              [innerHTML]="registerDescription(currentRegister!, true)"
            ></button>

            <div
              ngbDropdownMenu
              aria-labelledby="register-selection"
              class="register-selection-menu"
            >
              <button type="button" class="active" ngbDropdownItem>
                {{ currentRegister && registerDescription(currentRegister) }}
              </button>

              @if (recentRegisters.length) {
                <div class="dropdown-divider"></div>
                <span class="small text-body-secondary">Recent Registers</span>
                @for (register of recentRegisters; track $index) {
                  <button
                    type="button"
                    ngbDropdownItem
                    value="{{ register.id }}"
                    (click)="onUserChangedRegister(register)"
                  >
                    {{ registerDescription(register) }}
                  </button>
                }
              }

              <div class="dropdown-divider"></div>
              <button
                type="button"
                routerLink="/organisations/{{
                  currentOrganisation.organisationId || '-'
                }}/registers"
                ngbDropdownItem
                class="text-primary"
              >
                See All Registers
              </button>

              <ng-template
                authorizeShow
                [authorizeOrganisation]="
                  OrganisationPermissionType.CreateRegister
                "
              >
                <button
                  type="button"
                  (click)="openCreateRegisterModal()"
                  ngbDropdownItem
                >
                  Create New Register&hellip;
                </button>
              </ng-template>

              @if (demoRegister && !currentRegister.isDemo) {
                <div class="dropdown-divider"></div>

                <button
                  type="button"
                  ngbDropdownItem
                  value="{{ demoRegister.id }}"
                  class="text-guide"
                  (click)="onUserChangedRegister(demoRegister)"
                >
                  Demo Register
                </button>
              }
            </div>
          </div>
        }

        @if (!currentRegister) {
          <a
            class="btn"
            routerLink="/organisations/{{
              currentOrganisation.organisationId || '-'
            }}/dashboard"
            routerLinkActive="active"
          >
            <i-lucide name="list" class="nav-icon"></i-lucide>Registers
          </a>
        }
      }

      @if (hasOnlyDemoRegister) {
        <button
          type="button"
          class="btn btn-register"
          (click)="openCreateRegisterModal()"
        >
          <i-lucide name="plus" class="nav-icon"></i-lucide>Create Register
        </button>
      }
    </section>
  }

  <section class="nav-flex">
    @if (
      primaryRegisterNavigation.length ||
      primaryOrganisationNavigation.length ||
      settingsNavigation.length
    ) {
      <div>
        @if (currentRegister) {
          @for (
            nav of primaryRegisterNavigation;
            track $index;
            let last = $last
          ) {
            @if (nav.isExpandable) {
              <button
                type="button"
                class="btn"
                [class.mb-0]="last && !poolsExpanded"
                [ngbTooltip]="minimal ? nav.name : null"
                placement="end"
                (click)="togglePoolsExpanded()"
              >
                <i-lucide [name]="nav.icon" class="nav-icon"></i-lucide
                >{{ nav.name }}
                <i-lucide
                  [name]="poolsExpanded ? 'chevron-up' : 'chevron-down'"
                  class="ms-auto nav-expand-icon"
                ></i-lucide>
              </button>
              @if (poolsExpanded) {
                @for (
                  childNav of nav.children;
                  track $index;
                  let childLast = $last
                ) {
                  <a
                    class="btn pool-child"
                    [routerLink]="childNav.route"
                    [class.active]="childNav.id === currentView?.id"
                    [class.mb-0]="childLast && last"
                    [ngbTooltip]="minimal ? childNav.name : null"
                    placement="end"
                  >
                    <i-lucide [name]="childNav.icon" class="nav-icon"></i-lucide
                    >{{ childNav.name }}
                  </a>
                }
              }
            } @else {
              <ng-template
                authorizeShow
                [authorizeRegisterAny]="nav.registerPermissions"
                [authorizeFeature]="nav.feature"
                [authorizeFeatureFlag]="nav.featureFlag"
                [authorizeRegisterFeatureFlag]="nav.registerFeatureFlag"
                [authorizeRegisterFeatureFlagInverted]="
                  nav.registerFeatureFlagInverted
                "
              >
                <a
                  class="btn"
                  [routerLink]="nav.route"
                  [class.active]="nav.id === currentView?.id"
                  [class.mb-0]="last"
                  [ngbTooltip]="minimal ? nav.name : null"
                  placement="end"
                >
                  <i-lucide [name]="nav.icon" class="nav-icon"></i-lucide
                  >{{ nav.name }}
                </a>
              </ng-template>
            }
          }
          <hr class="settings-divider" />
        }

        @if (!currentRegister) {
          @for (
            nav of primaryOrganisationNavigation;
            track $index;
            let last = $last
          ) {
            <ng-template
              authorizeShow
              [authorizeOrganisationAny]="nav.organisationPermissions"
              [authorizeFeature]="nav.feature"
              [authorizeFeatureFlag]="nav.featureFlag"
            >
              <a
                class="btn"
                [routerLink]="nav.route"
                [class.active]="nav.id === currentView?.id"
                [class.mb-0]="last"
                [ngbTooltip]="minimal ? nav.name : null"
                placement="end"
              >
                <i-lucide [name]="nav.icon" class="nav-icon"></i-lucide
                >{{ nav.name }}
              </a>
            </ng-template>
          }
          <hr class="settings-divider" />
        }

        @for (nav of settingsNavigation; track $index) {
          @if (nav.organisationPermissions) {
            <ng-template
              authorizeShow
              [authorizeOrganisationAny]="nav.organisationPermissions"
            >
              <a
                class="btn"
                [routerLink]="orgSettingsLink"
                [class.active]="nav.id === currentView?.id"
                [ngbTooltip]="minimal ? ('Organisation Settings' | ize) : null"
                placement="end"
              >
                <i-lucide name="settings" class="nav-icon"></i-lucide>
                {{ 'Organisation' | ize }} Settings
              </a>
            </ng-template>
          }
          @if (nav.registerPermissions) {
            <ng-template
              authorizeShow
              [authorizeRegisterAny]="nav.registerPermissions"
            >
              <a
                class="btn"
                [routerLink]="nav.route"
                [class.active]="nav.id === currentView?.id"
                [ngbTooltip]="minimal ? nav.name : null"
                placement="end"
              >
                <i-lucide [name]="nav.icon" class="nav-icon"></i-lucide
                >{{ nav.name }}
              </a>
            </ng-template>
          }
        }
      </div>
    }
  </section>

  @if (showGuide$ | async) {
    <div class="nav-fixed guide">
      <button
        type="button"
        class="btn mb-0"
        [ngbTooltip]="minimal ? 'Show Setup Guide' : null"
        placement="end"
        (click)="onShowGuides()"
      >
        <i-lucide name="compass" class="nav-icon"></i-lucide>
        Show Setup Guide
      </button>
    </div>
  }

  <section class="nav-fixed bottom-nav">
    <div ngbDropdown placement="top-start">
      <div [class.horizontal]="!minimal">
        <button
          type="button"
          id="user-profile"
          class="btn mb-0"
          [ngbTooltip]="minimal ? userName : null"
          placement="end"
          ngbDropdownToggle
        >
          <ngx-avatars
            [name]="userName"
            [cornerRadius]="20"
            bgColor="#278c9c"
            fgColor="white"
            [size]="23"
            [textSizeRatio]="2.2"
            [round]="true"
            [gravatarId]="userEmail"
          >
          </ngx-avatars>
          <span class="ms-1">
            @if (currentOrganisation?.name) {
              <label>
                {{ currentOrganisation?.name }}
              </label>
            }
            <p>
              {{ userName }}
            </p>
          </span>
        </button>
        <button
          type="button"
          class="btn px-1 mb-0 d-block text-center"
          [class.w-auto]="!minimal"
          [class.ps-0]="!minimal"
          [class.mt-3]="minimal"
          [ngbTooltip]="(minimal ? 'Maximise' : 'Minimise') | ize"
          placement="end"
          (click)="minimal = !minimal"
        >
          <i-lucide
            [name]="minimal ? 'chevrons-right' : 'chevrons-left'"
            class="mx-0"
          ></i-lucide>
        </button>
      </div>
      <div
        ngbDropdownMenu
        aria-labelledby="user-profile"
        id="avatar-dropdown-menu"
        class="dropdown-menu-start"
      >
        <button
          type="button"
          (click)="openSwitchOrganisationModal()"
          ngbDropdownItem
        >
          <i-lucide name="repeat"></i-lucide>
          Switch {{ 'Organisation' | ize }}
        </button>

        <div class="dropdown-divider"></div>

        <button
          type="button"
          (click)="confirm(identityServerUrl + '/Account/Manage/UserProfile')"
          ngbDropdownItem
        >
          <i-lucide name="user"></i-lucide>
          My Profile
          <i-lucide name="external-link"></i-lucide>
        </button>

        <button
          type="button"
          (click)="
            confirm(identityServerUrl + '/Account/Manage/ChangePassword')
          "
          ngbDropdownItem
        >
          <i-lucide name="key"></i-lucide>
          Change Password
          <i-lucide name="external-link"></i-lucide>
        </button>

        <div class="dropdown-divider"></div>

        <button type="button" ngbDropdownItem (click)="logOut()">
          <i-lucide name="log-out"></i-lucide>
          Logout
        </button>
      </div>
    </div>
  </section>
</nav>
